import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ADMISSION_CODE_VALUES, type AdmissionCode } from "@shared/types/rejections.ts";
import { ConfirmDialog } from "../../../../../components/primitives/feedback/ConfirmDialog";
import { LoadingState } from "../../../../../components/primitives/feedback/LoadingState";
import { usePopupMenu } from "../../../../../components/primitives/navigation/PopupMenu";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../../components/primitives/data-display/DataSequence";
import { DocumentLink } from "../../../../../components/domain/data-display/DocumentLink";
import { AdmissionOutcomeBadge } from "../../../../../components/domain/data-display/AdmissionOutcomeBadge";
import { AdmissionReviewBadge } from "../../../../../components/domain/data-display/AdmissionReviewBadge";
import { CarriedForwardBadge } from "../../../../../components/domain/data-display/CarriedForwardBadge";
import { RejectionFindingSummary } from "../../../../../components/domain/data-display/RejectionFindingSummary";
import { DocumentDetailDialog } from "../../../Library/Documents/DocumentDetailDialog";
import { ADMISSION_RECHECK_TAB_ORDER, ADMISSION_RECHECK_TABS } from "./admission_recheck_tabs";
import type {
  AdmissionRecheckRow,
  UseAdmissionRecheckPanelResult,
} from "./useAdmissionRecheckPanel";
import { getBatchJobItemRowId } from "../../panel/batch_job_item_rows";

interface RowActionsProps {
  row: AdmissionRecheckRow;
  loading: boolean;
  error: string | null;
  onRevoke: () => void;
  onKeep: () => void;
  onOpenDetail: () => void;
}

function RowActions({ row, loading, error, onRevoke, onKeep, onOpenDetail }: RowActionsProps) {
  const isReviewable = row.item.review === "open";
  const { menu, setPopupMenuAnchor } = usePopupMenu([
    [{ label: "Details", onClick: onOpenDetail }],
    ...(isReviewable ? [[{ label: "Keep", onClick: onKeep }]] : []),
    ...(isReviewable ? [[{ label: "Revoke", onClick: onRevoke, color: "error" as const }]] : []),
  ]);

  if (loading) return <CircularProgress size={18} />;
  return (
    <Stack direction="row" gap={0.5} sx={{ alignItems: "center" }}>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      <IconButton size="small" onClick={setPopupMenuAnchor} aria-label="Row actions">
        <MoreVertIcon />
      </IconButton>
      {menu}
    </Stack>
  );
}

export interface AdmissionRecheckPanelBodyProps {
  panel: UseAdmissionRecheckPanelResult;
}

/** Presentational admission_recheck results panel: tabbed review queues, each a paginated,
 *  selectable list of items with per-item and bulk revoke/keep actions. Clicking a row opens
 *  the document detail dialog. */
export function AdmissionRecheckPanelBody({ panel }: AdmissionRecheckPanelBodyProps) {
  const columns: DataSequenceColumn<AdmissionRecheckRow>[] = [
    {
      key: "subject",
      header: "Document",
      width: 260,
      cardRole: "primary",
      cell: (row) => <DocumentLink title={row.item.subjectTitle} url={row.item.subjectUrl} />,
    },
    {
      key: "outcome",
      header: "Outcome",
      cell: (row) =>
        row.result ? (
          <AdmissionOutcomeBadge outcome={row.result.outcome} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {row.item.status === "failed" ? "—" : "…"}
          </Typography>
        ),
    },
    {
      key: "rejection",
      header: "Reason",
      cell: (row) => {
        const finding = row.result?.rejection;
        if (!finding) {
          return row.item.error ? (
            <Typography variant="caption" color="error">
              {row.item.error}
            </Typography>
          ) : (
            "—"
          );
        }
        return (
          <Stack gap={0.5} sx={{ alignItems: "flex-start" }}>
            <RejectionFindingSummary code={finding.code} message={finding.message} />
            {row.result?.carriedForwardFromItemId && <CarriedForwardBadge />}
          </Stack>
        );
      },
    },
    {
      key: "review",
      header: "Review",
      cell: (row) => (row.item.review ? <AdmissionReviewBadge review={row.item.review} /> : "—"),
    },
    {
      key: "actions",
      header: "",
      cardRole: "action",
      cell: (row) => (
        <RowActions
          row={row}
          loading={panel.review.keep.keepingId === row.item.id && panel.review.keep.loading}
          error={panel.review.keep.keepingId === row.item.id ? panel.review.keep.error : null}
          onRevoke={() => panel.review.apply.open(row)}
          onKeep={() => panel.review.keep.handle(row)}
          onOpenDetail={() => panel.detail.open(row)}
        />
      ),
    },
  ];

  return (
    <Stack gap={2}>
      <Tabs
        value={panel.tabs.activeTab}
        onChange={(_e, v: string) => panel.tabs.setActiveTab(v as typeof panel.tabs.activeTab)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
      >
        {ADMISSION_RECHECK_TAB_ORDER.map((key) => (
          <Tab key={key} label={ADMISSION_RECHECK_TABS[key].label} value={key} />
        ))}
      </Tabs>

      {panel.tabs.tab.rejectionFilterable && (
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="rejection-code-filter-label">Rejection reason</InputLabel>
          <Select
            labelId="rejection-code-filter-label"
            label="Rejection reason"
            value={panel.filters.rejectionCode ?? ""}
            onChange={(e) =>
              panel.filters.setRejectionCode((e.target.value as AdmissionCode) || null)
            }
          >
            <MenuItem value="">All reasons</MenuItem>
            {ADMISSION_CODE_VALUES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {panel.list.error && <Alert severity="error">{panel.list.error}</Alert>}

      {panel.tabs.tab.reviewable && panel.selection.count > 0 && (
        <Stack
          direction="row"
          gap={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography variant="body2">{panel.selection.count} selected</Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={panel.review.bulkApply.open}
          >
            Revoke selected ({panel.selection.count})
          </Button>
        </Stack>
      )}

      {panel.list.loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={panel.list.rows}
          getRowId={getBatchJobItemRowId}
          selection={panel.selection.table}
          emptyMessage="No items in this queue."
          pagination={panel.list.pagination}
        />
      )}

      <ConfirmDialog {...panel.review.apply.dialogProps} />

      <ConfirmDialog
        {...panel.review.bulkApply.dialogProps}
        progress={panel.review.bulkApply.progress}
      />

      <DocumentDetailDialog item={panel.detail.target} onClose={panel.detail.close} />
    </Stack>
  );
}
